import React, { useEffect, useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { ticketTemplateAPI, planAPI } from '../services/api';
import { LayoutTemplate, Edit, Trash2 } from 'lucide-react';

const TicketDesignerListPage = () => {
  const { isDark } = useTheme();
  const [templates, setTemplates] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [templateRes, plansRes] = await Promise.all([
        ticketTemplateAPI.list(),
        planAPI.getAll(),
      ]);
      setTemplates(templateRes.data || []);
      setPlans(plansRes.data || []);
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getPlanNames = (planIds) => {
    if (!planIds || planIds.length === 0) return 'Sin asignar';
    return planIds
      .map((id) => plans.find((p) => p.id === id)?.name)
      .filter(Boolean)
      .join(', ');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este diseño?')) return;
    try {
      await ticketTemplateAPI.delete(id);
      setTemplates((prev) => prev.filter((t) => t.id !== id));
    } catch (err) {
      console.error('Error deleting template:', err);
      alert('Error al eliminar el diseño');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {templates.length === 0 ? (
        <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-12 text-center`}>
          <LayoutTemplate className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            No hay diseños guardados
          </h3>
          <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Crea tu primer diseño usando la pestaña "Crear Diseño"
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((template) => (
            <div
              key={template.id}
              className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-sm border-2 p-6 hover:shadow-lg transition-all`}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className={`font-bold text-lg mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {template.name}
                  </h3>
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Planes: {getPlanNames(template.assigned_plan_ids)}
                  </p>
                </div>
              </div>

              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => window.location.href = `/ticket-designer?edit=${template.id}`}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm"
                >
                  <Edit className="w-4 h-4" />
                  Editar
                </button>
                <button
                  onClick={() => handleDelete(template.id)}
                  className="px-4 py-2 rounded-lg border border-red-600 text-red-600 hover:bg-red-50 text-sm"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TicketDesignerListPage;
